export interface QuantityDiscount {
  minQuantity: number;
  maxQuantity: number | null;
  customPrice: number;
}

export interface CalendarImage {
  id: string;
  url: string;
  name: string;
}

export interface Category {
  id: number;
  name: string;
  memo: string;
}

export interface NavigationLink {
  label: string;
  url: string;
}

export interface Department {
  id: number;
  name: string;
  memo: string;
  productCategories?: Category[];
  navigationLinks?: NavigationLink[];
}

export interface ProductVariation {
  id: number | string;
  size: string;
  color: string;
  hex?: string;
  price: number;
  stock: number;
  images?: string[] | CalendarImage[];
  sku?: string;
  description?: string;
}

export interface RawProduct {
  id: number;
  sku: string;
  item: string;
  price: number;
  images: string[];
  departments: number[];
  categories: number[];
  clientId: number;
  variations: Array<{
    id: number;
    size: string;
    color: string;
    hex?: string;
    price: number;
    stock: number;
    images?: string[] | CalendarImage[];
    description?: string;
  }>;
  description?: string;
  shortDescription?: string;
  isPrintDetail?: boolean;
  printOnDemand?: {
    type?: 'business_card' | 'calendar' | 'customize_item' | null;
    style?: string | null;
    useCardBuilder?: boolean;
    cardTemplateId?: number | null;
    fields?: { key: string; label: string }[];
    options?: { color?: string; text_message?: string };
    images?: {
      logo?: string | null;
      front?: string | null;
      back?: string | null;
      frontForReview?: string | null;
    } | null;
  } | null;
  stock?: number | null;
  allowBackorder?: boolean;
  allowOnDemand?: boolean;
  promoCodeOnly?: boolean;
  minOrderQty?: number | null;
  maxOrderQty?: number | null;
  productionTimeline?: string | null;
  personalization?: {
    instructions: string | null;
    fee: string;
    charLimit: number | null;
    placements: string[] | null;
  } | null;
  customerCharges?: { name: string; amount: number }[];
  quantityDiscounts?: QuantityDiscount[];
}

// theme: 0 = default, 1 = dark, 2 = light
export const LOGO_THEME_DEFAULT = 0;
export const LOGO_THEME_DARK = 1;
export const LOGO_THEME_LIGHT = 2;

export interface LogoOption {
  id: number;
  name: string;
  theme: number | null;
  isDefault: boolean;
  imageUrl: string | null;
}

export interface SecondLogoOption {
  id: number;
  name: string;
  theme: number | null;
  isDefault: boolean;
  price: number; // customer price in dollars
  imageUrl: string | null;
}

export interface CombinedLogoOptions {
  primary: LogoOption[];
  secondary: SecondLogoOption[];
}

export interface Product {
  id: number;
  name: string;
  description: string;
  shortDescription?: string;
  images: string[];
  printImages?: {
    frontForReview?: string | null;
  };
  departments: number[];
  categories: number[];
  variations: ProductVariation[];
  basePrice: number;
  isPrintDetail?: boolean;
  printDetailType?: 'business_card' | 'calendar' | 'customize_item' | null;
  printOnDemandStyle?: string | null;
  useCardBuilder?: boolean;
  cardTemplateId?: number | null;
  printOnDemandFields?: { key: string; label: string }[];
  printOnDemandOptions?: { color?: string; text_message?: string };
  stock?: number | null;
  allowBackorder?: boolean;
  allowOnDemand?: boolean;
  promoCodeOnly?: boolean;
  minOrderQty?: number | null;
  maxOrderQty?: number | null;
  productionTimeline?: string | null;
  personalizationEnabled?: boolean;
  personalizationInstructions?: string | null;
  personalizationFee?: number | null;
  personalizationCharLimit?: number | null;
  personalizationPlacements?: string[];
  // Fixed one-time charges shown to the shopper, each applied once per cart line item (not multiplied by quantity).
  customerCharges?: { name: string; amount: number }[];
  quantityDiscounts?: QuantityDiscount[];
}

export interface PaginatedProductResponse {
  data: Product[];
  total: number;
  size: number;
  currentPage: number;
  totalPages: number;
}